import { useEffect, useMemo, useState } from "react";
import Api from "../../../../shared/api/api";
import AdminSidebar from "../components/AdminSidebar";
import "../styles/AdminImages.css";

export default function AdminImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [relatedTypeFilter, setRelatedTypeFilter] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadAltText, setUploadAltText] = useState("");
  const [uploadRelatedType, setUploadRelatedType] = useState("admin");
  const [uploading, setUploading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [replacementUrl, setReplacementUrl] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [usageLoadingIds, setUsageLoadingIds] = useState([]);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    setLoading(true);

    try {
      const data = await Api.getImages();
      setImages(Array.isArray(data?.images) ? data.images : []);
    } catch (err) {
      console.error("Error loading images:", err);
      setImages([]);
    }

    setLoading(false);
  };

  const filteredImages = useMemo(() => {
    let list = [...images];

    if (relatedTypeFilter) {
      list = list.filter(
        (img) =>
          String(img.related_type || "").toLowerCase() ===
          relatedTypeFilter.toLowerCase()
      );
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();

      list = list.filter((img) => {
        return (
          String(img.filename || "").toLowerCase().includes(q) ||
          String(img.url || "").toLowerCase().includes(q) ||
          String(img.canonical_url || "").toLowerCase().includes(q) ||
          String(img.alt_text || "").toLowerCase().includes(q) ||
          String(img.related_type || "").toLowerCase().includes(q)
        );
      });
    }

    return list;
  }, [images, search, relatedTypeFilter]);

  const relatedTypes = useMemo(() => {
    const set = new Set();

    images.forEach((img) => {
      if (img.related_type) set.add(img.related_type);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [images]);

  const isUsageLoading = (imageId) => usageLoadingIds.includes(imageId);

  const setUsageLoading = (imageId, value) => {
    setUsageLoadingIds((prev) => {
      if (value) {
        if (prev.includes(imageId)) return prev;
        return [...prev, imageId];
      }

      return prev.filter((id) => id !== imageId);
    });
  };

  const checkUsage = async (imageId, silent = false) => {
    setUsageLoading(imageId, true);

    try {
      const data = await Api.getImageUsage(imageId);
      const usage = data?.usage || {
        books: 0,
        blogs: 0,
        auto_posts: 0,
        other: 0,
        total: 0,
      };

      setImages((prev) =>
        prev.map((img) =>
          img.id === imageId
            ? {
                ...img,
                usage,
              }
            : img
        )
      );

      setDeleteTarget((prev) => {
        if (!prev || prev.id !== imageId) return prev;

        return {
          ...prev,
          usage,
        };
      });

      return usage;
    } catch (err) {
      console.error("Usage check error:", err);

      if (!silent) {
        alert("Failed to check image usage.");
      }

      return null;
    } finally {
      setUsageLoading(imageId, false);
    }
  };

  const copyToClipboard = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} copied to clipboard.`);
    } catch (err) {
      console.error("Clipboard error:", err);
      alert("Failed to copy.");
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadAltText("");
    setUploadRelatedType("admin");
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      return alert("Please choose an image.");
    }

    const formData = new FormData();
    formData.append("imageFile", uploadFile);
    formData.append("related_type", uploadRelatedType || "admin");
    formData.append("related_id", "");
    formData.append("alt_text", uploadAltText || "");
    formData.append("is_primary", "true");

    setUploading(true);

    try {
      await Api.uploadImage(formData);
      setShowUploadModal(false);
      resetUploadForm();
      await loadImages();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload image.");
    }

    setUploading(false);
  };

  const openDeleteModal = async (image) => {
    setDeleteTarget(image);
    setReplacementUrl("");

    if (!image.usage) {
      await checkUsage(image.id, true);
    }
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setReplacementUrl("");
    setDeleting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    let usage = deleteTarget.usage;

    if (!usage) {
      usage = await checkUsage(deleteTarget.id, true);
    }

    const usageTotal = Number(usage?.total || 0);

    if (usageTotal > 0 && !replacementUrl.trim()) {
      return alert(
        "This image is being used. Paste a replacement image URL before deleting."
      );
    }

    const confirmMessage =
      usageTotal > 0
        ? `Delete "${deleteTarget.filename}" and replace it everywhere it is used?`
        : `Delete "${deleteTarget.filename}"?`;

    if (!window.confirm(confirmMessage)) return;

    setDeleting(true);

    try {
      await Api.deleteImage(deleteTarget.id, replacementUrl.trim());
      closeDeleteModal();
      await loadImages();
    } catch (err) {
      console.error("Delete image error:", err);
      alert(
        "Failed to delete image. If it is used, a replacement URL may be required."
      );
      setDeleting(false);
    }
  };

  const getUsageTotal = (image) => Number(image?.usage?.total || 0);

  const renderUsageBox = (image) => {
    const loadingUsage = isUsageLoading(image.id);

    if (loadingUsage) {
      return (
        <div className="adminImageUsage">
          <div className="usageTotal">Checking usage...</div>
        </div>
      );
    }

    if (!image.usage) {
      return (
        <div className="adminImageUsage">
          <div className="usageTotal">Usage: Not checked</div>

          <button
            type="button"
            className="adminImageCheckUsageBtn"
            onClick={() => checkUsage(image.id)}
          >
            Check Usage
          </button>
        </div>
      );
    }

    return (
      <div className="adminImageUsage">
        <div className="usageTotal">Total: {getUsageTotal(image)}</div>
        <div>Books: {image?.usage?.books || 0}</div>
        <div>Blogs: {image?.usage?.blogs || 0}</div>
        <div>Posts: {image?.usage?.auto_posts || 0}</div>
        <div>Other: {image?.usage?.other || 0}</div>

        <button
          type="button"
          className="adminImageCheckUsageBtn"
          onClick={() => checkUsage(image.id)}
        >
          Recheck
        </button>
      </div>
    );
  };

  return (
    <>
      <AdminSidebar />

      <div className="adminImagesPage">
        <h1>Admin Images</h1>

        <div className="adminImagesTopBar">
          <button
            type="button"
            className="adminImagesAddBtn"
            onClick={() => setShowUploadModal(true)}
          >
            + Add Image
          </button>

          <button
            type="button"
            className="adminImagesRefreshBtn"
            onClick={loadImages}
          >
            Refresh
          </button>
        </div>

        <div className="adminImagesFilters">
          <input
            type="text"
            value={search}
            placeholder="Search image name, URL, alt text..."
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            value={relatedTypeFilter}
            onChange={(e) => setRelatedTypeFilter(e.target.value)}
          >
            <option value="">All Image Types</option>
            {relatedTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="adminImagesMessage">Loading images...</p>
        ) : filteredImages.length === 0 ? (
          <p className="adminImagesMessage">No images found.</p>
        ) : (
          <div className="adminImagesList">
            {filteredImages.map((image) => (
              <div key={image.id} className="adminImageRow">
                <div className="adminImagePreviewWrap">
                  <img
                    src={image.canonical_url || image.url}
                    alt={image.alt_text || image.filename}
                    className="adminImagePreview"
                    loading="lazy"
                  />
                </div>

                <div className="adminImageInfo">
                  <div className="adminImageName">{image.filename}</div>

                  <div className="adminImageUrl">
                    {image.canonical_url || image.url}
                  </div>

                  <div className="adminImageMeta">
                    Type: {image.related_type || "N/A"}
                    {image.alt_text ? ` · Alt: ${image.alt_text}` : ""}
                  </div>
                </div>

                <div className="adminImageActions">
                  <button
                    type="button"
                    className="adminImageCopyBtn"
                    onClick={() =>
                      copyToClipboard(
                        image.canonical_url || image.url,
                        "Image URL"
                      )
                    }
                  >
                    Copy URL
                  </button>

                  <button
                    type="button"
                    className="adminImageCopyNameBtn"
                    onClick={() =>
                      copyToClipboard(image.filename || "", "Image name")
                    }
                  >
                    Copy Name
                  </button>
                </div>

                {renderUsageBox(image)}

                <div className="adminImageDeleteWrap">
                  <button
                    type="button"
                    className="adminImageDeleteBtn"
                    onClick={() => openDeleteModal(image)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showUploadModal && (
          <div className="adminImagesModalOverlay">
            <div className="adminImagesModal">
              <h2>Add Image</h2>

              <label>Image File</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />

              <label>Image Type</label>
              <select
                value={uploadRelatedType}
                onChange={(e) => setUploadRelatedType(e.target.value)}
              >
                <option value="admin">admin</option>
                <option value="book">book</option>
                <option value="blog">blog</option>
                <option value="social">social</option>
                <option value="merch">merch</option>
                <option value="other">other</option>
              </select>

              <label>Alt Text</label>
              <input
                type="text"
                value={uploadAltText}
                onChange={(e) => setUploadAltText(e.target.value)}
                placeholder="Optional description"
              />

              <div className="adminImagesModalButtons">
                <button
                  type="button"
                  className="adminImagesSubmitBtn"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Image"}
                </button>

                <button
                  type="button"
                  className="adminImagesCancelBtn"
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="adminImagesModalOverlay">
            <div className="adminImagesModal">
              <h2>Delete Image</h2>

              <p>
                <strong>{deleteTarget.filename}</strong>
              </p>

              <img
                src={deleteTarget.canonical_url || deleteTarget.url}
                alt={deleteTarget.alt_text || deleteTarget.filename}
                className="deletePreviewImage"
              />

              {isUsageLoading(deleteTarget.id) ? (
                <div className="deleteUsageBox">Checking usage...</div>
              ) : (
                <div className="deleteUsageBox">
                  <div>Total Usage: {deleteTarget?.usage?.total || 0}</div>
                  <div>Books: {deleteTarget?.usage?.books || 0}</div>
                  <div>Blogs: {deleteTarget?.usage?.blogs || 0}</div>
                  <div>Posts: {deleteTarget?.usage?.auto_posts || 0}</div>
                  <div>Other: {deleteTarget?.usage?.other || 0}</div>
                </div>
              )}

              {Number(deleteTarget?.usage?.total || 0) > 0 && (
                <>
                  <p className="deleteWarning">
                    This image is currently used. Paste a replacement image URL
                    before deleting.
                  </p>

                  <label>Replacement Image URL</label>
                  <input
                    type="text"
                    value={replacementUrl}
                    onChange={(e) => setReplacementUrl(e.target.value)}
                    placeholder="https://www.kennethlpowell.com/images/replacement.jpg"
                  />
                </>
              )}

              <div className="adminImagesModalButtons">
                <button
                  type="button"
                  className="adminImagesDeleteConfirmBtn"
                  onClick={handleDelete}
                  disabled={deleting || isUsageLoading(deleteTarget.id)}
                >
                  {deleting ? "Deleting..." : "Delete Image"}
                </button>

                <button
                  type="button"
                  className="adminImagesCancelBtn"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}